// expandRitual — fælles ekspansions-motor for skema (Trin 1)
// Ren funktion. Ingen brugervendt tekst, ingen side-effekter.
// Kaldes af Planisphere, konfliktmotor og timeregnskab i senere trin,
// så alle tre viser NØJAGTIG samme forekomster.
//
// Bagudkompatibelt: mangler seriesStart tolkes ritualet som
// ugentlig, interval 1, uendelig — præcis som systemet opfører sig i dag.
//
// Tid håndteres i LOKAL kalendertid (dansk vægur), aldrig som epoch-offset,
// så sommer/vintertid ikke skrider.
// ============================================================

export interface RitualExceptionLike {
    date: string;            // 'YYYY-MM-DD'
    cancelled?: boolean;
    patch?: {
      time?: string;
      endTime?: string;
      title?: string;
      description?: string;
    };
  }
  
  export interface RitualLike {
    id: string;
    day: string;             // 'Mandag'..'Søndag' (da) eller 'Monday'.. (en)
    time: string;            // 'HH:MM'
    endTime?: string;        // 'HH:MM'
    repeat?: 'none' | 'weekly' | 'daily';
    repeatInterval?: number; // hver X. Default 1
    seriesStart?: string;    // 'YYYY-MM-DD' anker
    seriesEnd?: string;      // 'YYYY-MM-DD' valgfri slut
    isVisible?: boolean;
    hiddenUntil?: number;    // epoch ms
    exceptions?: RitualExceptionLike[];
    // videreføres uændret på hver forekomst:
    title?: any;
    description?: any;
    [k: string]: any;
  }
  
  export interface SchoolVacation {
    from: string;            // 'YYYY-MM-DD'
    to: string;              // 'YYYY-MM-DD' (inklusiv)
  }
  
  export interface Occurrence {
    ritualId: string;
    date: string;            // 'YYYY-MM-DD'
    time: string;            // 'HH:MM' (efter evt. patch)
    endTime: string;         // 'HH:MM' (efter evt. patch; udledt hvis mangler)
    startMinutes: number;    // minutter siden midnat, lokal
    endMinutes: number;
    isPatched: boolean;
    isCancelled?: boolean;      // kun naar includeCancelled=true
    coverGuardianId?: string;   // afloeser fra exception
    coverGuardianName?: string; // afloeser-navn fra exception
    statusLabel?: string;       // fra exception (fx 'Vikar')
    note?: string;              // besked fra exception      // enkelt-dags patch anvendt
    master: RitualLike;      // reference til master (for titel/farve/adgang i UI)
    titleOverride?: any;     // fra patch, hvis sat
    descriptionOverride?: any;
  }
  
  const DAY_TO_NUM: Record<string, number> = {
    'Søndag': 0, 'Sunday': 0,
    'Mandag': 1, 'Monday': 1,
    'Tirsdag': 2, 'Tuesday': 2,
    'Onsdag': 3, 'Wednesday': 3,
    'Torsdag': 4, 'Thursday': 4,
    'Fredag': 5, 'Friday': 5,
    'Lørdag': 6, 'Saturday': 6,
  };
  
  // --- rene dato-hjælpere i lokal tid (ingen UTC-drift) ---
  function ymd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  function parseYMD(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1); // lokal midnat
  }
  function addDays(d: Date, n: number): Date {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
  }
  // hele dage mellem to lokale datoer (a - b), robust mod sommertid
  function dayDiff(a: Date, b: Date): number {
    const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.round((utcA - utcB) / 86400000);
  }
  function toMinutes(hhmm: string): number {
    const [h, m] = (hhmm || '00:00').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  }
  function deriveEndTime(time: string, endTime?: string): string {
    if (endTime && endTime.trim()) return endTime;
    const start = toMinutes(time);
    const end = Math.min(24 * 60 - 1, start + 60); // default 60 min
    const h = Math.floor(end / 60);
    const m = end % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  
  function isInAnyVacation(dateStr: string, vacations: SchoolVacation[]): boolean {
    for (const v of vacations) {
      if (!v.from || !v.to) continue;
      if (dateStr >= v.from && dateStr <= v.to) return true; // string-sammenligning gyldig for ISO
    }
    return false;
  }
  
  /**
   * Ekspandér et ritual til daterede forekomster i [fromDate, toDate] (begge inklusiv, 'YYYY-MM-DD').
   * @param vacations  school_years-perioder; forekomster i ferie springes over.
   * @param skipVacations  false for staff-view der vil se ferie-lektioner alligevel.
   */
  export function expandRitual(
    master: RitualLike,
    fromDate: string,
    toDate: string,
    vacations: SchoolVacation[] = [],
    skipVacations: boolean = true,
    includeCancelled: boolean = false
  ): Occurrence[] {
    const out: Occurrence[] = [];
    if (!master || !master.day || !master.time) return out;
  
    const repeat = master.repeat || 'weekly';
    const interval = Math.max(1, master.repeatInterval || 1);
    const targetDow = DAY_TO_NUM[master.day];
  
    const winStart = parseYMD(fromDate);
    const winEnd = parseYMD(toDate);
  
    // Anker: seriesStart hvis sat, ellers vinduets start (bagudkompat = uendelig ugentlig)
    // Fast anker naar seriesStart mangler og interval > 1: ellers bliver 'hver 2. uge'
    // vindues-relativ (uge- og maanedsvisning ville ramme forskellige uger).
    const anchor = master.seriesStart ? parseYMD(master.seriesStart) : (interval > 1 ? parseYMD('2024-01-01') : winStart);
    const seriesEnd = master.seriesEnd ? parseYMD(master.seriesEnd) : null;
  
    const exMap: Record<string, RitualExceptionLike> = {};
    (master.exceptions || []).forEach(e => { if (e && e.date) exMap[e.date] = e; });
  
    // effektiv start = senest af (vinduets start, anker, seriesStart)
    let cursor = winStart < anchor ? new Date(anchor) : new Date(winStart);
    // 'none' (engangs) behandles som en enkelt dato = seriesStart (eller anker)
    if (repeat === 'none') {
      // firstWeekdayMatch: engangs uden seriesStart maa ikke flyde til vinduets start
      // uanset ugedag \u2014 find foerste dato i vinduet med lektionens ugedag.
      let only: Date;
      if (master.seriesStart) { only = parseYMD(master.seriesStart); }
      else { only = new Date(winStart); let g0 = 0; while (only.getDay() !== (DAY_TO_NUM[master.day] ?? -1) && g0 < 7) { only.setDate(only.getDate() + 1); g0++; } if (g0 >= 7) return out; }
      const s = ymd(only);
      if (s >= fromDate && s <= toDate) {
        pushIfValid(only);
      }
      return out;
    }
  
    for (let d = new Date(cursor); d <= winEnd; d = addDays(d, 1)) {
      if (seriesEnd && d > seriesEnd) break;
  
      if (repeat === 'weekly') {
        if (d.getDay() !== targetDow) continue;
        // interval: kun hver X'te uge målt fra ankerets uge
        const weeks = Math.floor(dayDiff(d, anchor) / 7);
        if (weeks < 0) continue;
        if (weeks % interval !== 0) continue;
      } else if (repeat === 'daily') {
        const days = dayDiff(d, anchor);
        if (days < 0) continue;
        if (days % interval !== 0) continue;
      }
      pushIfValid(d);
    }
    return out;
  
    function pushIfValid(d: Date) {
      const dateStr = ymd(d);
      if (dateStr < fromDate || dateStr > toDate) return;
  
      const ex = exMap[dateStr];
      if (ex && ex.cancelled && !includeCancelled) return; // aflyst enkelt-dag
  
      // ferie: spring over (kun når skipVacations)
      // per-dag ferie-undtagelse: exception m. showInVacation viser netop denne dato
    if (skipVacations && isInAnyVacation(dateStr, vacations) && !(ex && (ex as any).showInVacation)) return;
  
      // skjul per dato (ikke mod Date.now())
      if (master.isVisible === false) return;
      if (master.hiddenUntil) {
        const dayStartMs = parseYMD(dateStr).getTime();
        const dayEndMs = dayStartMs + 86400000 - 1;
        if (dayEndMs < master.hiddenUntil) return; // hele dagen ligger før hiddenUntil
      }
  
      const patch = ex && ex.patch ? ex.patch : undefined;
      const time = (patch && patch.time) || master.time;
      const endTime = deriveEndTime(time, (patch && patch.endTime) || master.endTime);
  
      out.push({
        ritualId: master.id,
        date: dateStr,
        time,
        endTime,
        startMinutes: toMinutes(time),
        endMinutes: toMinutes(endTime),
        isPatched: !!patch,
        isCancelled: !!(ex && ex.cancelled),
        statusLabel: ex && (ex as any).statusLabel ? (ex as any).statusLabel : undefined,
        coverGuardianId: ex && (ex as any).coverGuardianId ? (ex as any).coverGuardianId : undefined,
        coverGuardianName: ex && (ex as any).coverGuardianName ? (ex as any).coverGuardianName : undefined,
        note: ex && (ex as any).note ? (ex as any).note : undefined,
        master,
        titleOverride: patch && patch.title !== undefined ? patch.title : undefined,
        descriptionOverride: patch && patch.description !== undefined ? patch.description : undefined,
      });
    }
  }